import React, { useEffect, useState } from 'react';
import * as S from '../Advances/AdvancesStyles';
import { getTeamAttendance, getPendingLeaves } from '../../../../services/attendanceService';

const todayStr = () => new Date().toISOString().split('T')[0];

/** A plain horizontal bar row — deliberately not a hand-rolled SVG chart like
 *  Advances' OverviewTab: this data updates once a day (single-date team attendance,
 *  see attendanceService.js), so a simple, honest bar is more truthful than an
 *  elaborate chart implying more granularity than the backend actually provides. */
function BarRow({ label, percent, count, total, color }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ fontWeight: 700, color: '#1E2130' }}>{label}</span>
        <span style={{ color: '#6B7280' }}>
          {count}/{total} · {percent}%
        </span>
      </div>
      <div style={{ height: 8, background: '#EEF0F3', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(percent, 2)}%`, height: '100%', background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}

const OverviewTab = ({ projects }) => {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [date, setDate] = useState(todayStr());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [recs, leaves] = await Promise.all([getTeamAttendance(date), getPendingLeaves()]);
      if (!cancelled) {
        setRecords(recs);
        setPendingLeaves(leaves);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [date]);

  const total = records.length;
  const present = records.filter(r => r.status === 'PRESENT').length;
  const halfDay = records.filter(r => r.status === 'HALF_DAY').length;
  const onLeave = records.filter(r => r.status === 'ON_LEAVE').length;
  const absent = records.filter(r => r.status === 'ABSENT').length;
  const pct = n => (total ? Math.round((n / total) * 100) : 0);

  // Project-wise breakdown — attendance records carry their own project_id, so this is a
  // real grouping of today's records, not a fabricated split.
  const byProject = (projects || []).map(p => {
    const projRecords = records.filter(r => String(r.project_id) === String(p.id));
    const pTotal = projRecords.length;
    const pPresent = projRecords.filter(r => r.status === 'PRESENT' || r.status === 'HALF_DAY').length;
    return { id: p.id, name: p.name, total: pTotal, present: pPresent, pct: pTotal ? Math.round((pPresent / pTotal) * 100) : 0 };
  }).filter(p => p.total > 0);

  if (loading) {
    return (
      <S.CardBox style={{ textAlign: 'center', padding: 60, color: '#6B7280' }}>
        Loading attendance overview…
      </S.CardBox>
    );
  }

  return (
    <div>
      <S.FiltersBar style={{ marginBottom: 16 }}>
        <div className="field">
          <label>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} max={todayStr()} />
        </div>
      </S.FiltersBar>

      <S.KpiRow cols={4}>
        <S.KpiCard accent>
          <div className="lbl">Marked Present</div>
          <div className="val">{present + halfDay}</div>
          <div className="sub">of {total} on record for {date}</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">On Leave</div>
          <div className="val">{onLeave}</div>
          <div className="sub">approved leave covering this date</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Absent</div>
          <div className="val">{absent}</div>
          <div className="sub">no punch, no approved leave</div>
        </S.KpiCard>
        <S.KpiCard>
          <div className="lbl">Pending Leave Approvals</div>
          <div className="val">{pendingLeaves.length}</div>
          <div className="sub">waiting across all projects</div>
        </S.KpiCard>
      </S.KpiRow>

      <S.Grid2 ratio="1fr 1fr">
        <S.CardBox>
          <h3>Today's Split</h3>
          <p className="hint">Every attendance record fetched for {date}.</p>
          <BarRow label="Present" percent={pct(present)} count={present} total={total} color="#1E9E5A" />
          <BarRow label="Half Day" percent={pct(halfDay)} count={halfDay} total={total} color="#B9791A" />
          <BarRow label="On Leave" percent={pct(onLeave)} count={onLeave} total={total} color="#8B5CE0" />
          <BarRow label="Absent" percent={pct(absent)} count={absent} total={total} color="#D5372A" />
        </S.CardBox>

        <S.CardBox>
          <h3>Project-wise Attendance</h3>
          <p className="hint">Present + half-day share of each project's marked records for {date}.</p>
          {byProject.length === 0 ? (
            <div style={{ color: '#9297A3', fontSize: 13, padding: '20px 0' }}>No records tagged to a project for this date yet.</div>
          ) : (
            byProject.map(p => (
              <BarRow key={p.id} label={p.name} percent={p.pct} count={p.present} total={p.total} color="#2F6FED" />
            ))
          )}
        </S.CardBox>
      </S.Grid2>
    </div>
  );
};

export default OverviewTab;
